import { Component, OnDestroy, AfterContentInit } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { LoadService } from 'src/app/services/application/load.service';
import { FileUploadService } from 'src/app/services/file-upload.service';
import { AlertService } from 'src/lib/ariadna/alert';
import { DownloadTypes, ImgTypes } from './patient-files-types';
import {
    IDocumentResult,
    TDocument,
    TDocumentList,
} from 'src/app/interfaces/files.interfaces';
import { Title } from '@angular/platform-browser';

@Component({
    selector: 'patient-files',
    templateUrl: './patient-files.component.html',
    styleUrls: ['./patient-files.component.scss'],
    standalone: false,
})
export class PatientFilesComponent implements AfterContentInit, OnDestroy {
    public filesSection: boolean = false;
    private abortController: AbortController | null = null;
    public loading: boolean = false;
    public load_document: boolean = false;
    public $documents: Subscription | undefined;
    public documentsList: TDocumentList = [];
    public documentListOnPage: TDocumentList = [];
    public documentLimitOnPage: number = 8;
    public selectedIndex: number | null = null;
    public activeImgInBase64: string | null = null;
    public downloadLink: string | null = null;
    public fileNameWithExt: string | null = null;
    public isMobile: boolean = false;
    public showRightMobile: boolean = false;
    public selectedItem: TDocument | null = null;

    public constructor(
        private fileServices: FileUploadService,
        private load: LoadService,
        private router: Router,
        private alertService: AlertService,
        private titleService: Title,
    ) {
        if (
            load.configData?.CLIENT_SETTINGS.ENABLED_MODULES.indexOf(
                'documents',
            ) === -1
        ) {
            this.router.navigateByUrl('/');
        } else {
            this.filesSection = true;
        }
        const tmpTitle =
            load.configData?.CLIENT_SETTINGS.PAGES_SETTINGS.DOCUMENTS.TITLE;
        if (tmpTitle) this.titleService.setTitle(tmpTitle);
    }

    private mobileMediaQuery!: MediaQueryList;

    public ngOnInit(): void {
        this.initializeMobileCheck();
    }

    public initializeMobileCheck(): void {
        // Определяем мобильное устройство по ширине экрана
        this.mobileMediaQuery = window.matchMedia('(max-width: 768px)');
        this.isMobile = this.mobileMediaQuery.matches;

        // Слушаем изменения размеров экрана
        this.mobileMediaQuery.addEventListener(
            'change',
            this.handleMediaChange,
        );
    }

    public handleMediaChange = (event: MediaQueryListEvent): void => {
        this.isMobile = event.matches;
    };

    public destroyMobileCheck(): void {
        this.mobileMediaQuery.removeEventListener(
            'change',
            this.handleMediaChange,
        );
    }

    public ngAfterContentInit() {
        this.getDocumentsList();
    }

    public ngOnDestroy(): void {
        this.destroyMobileCheck(); // Удаляем слушатель
        this.$documents?.unsubscribe();
    }
    public getDocumentsList(): void {
        this.loading = true;
        this.fileServices
            .getDocumentsList()
            .pipe(finalize(() => (this.loading = false)))
            .subscribe({
                next: (value: TDocumentList) => {
                    // Используем parseDateString для точного парсинга
                    value.forEach((item) => {
                        const parsedDate = this.parseDateString(item.datStr);
                        if (parsedDate) {
                            item.dtSort = parsedDate;
                        } else {
                            item.dtSort = new Date(); // или значение по умолчанию
                        }
                    });

                    // Сортируем по дате (по убыванию)
                    this.documentsList = value.sort(
                        (a, b) => b.dtSort.getTime() - a.dtSort.getTime(),
                    );
                    // console.log('документы', this.documentsList);
                    // Обновляем список на странице
                    this.documentListOnPage = this.documentsList.slice(
                        0,
                        this.documentLimitOnPage,
                    );
                },
                error: (err: Error) => {
                    this.alertService.error(
                        'Ошибка при получении списка документов',
                    );
                    console.error('Error in getDocumentsList', err);
                },
            });
    }
    public async getDocument(
        $event: any,
        id: number,
        file_name: string,
        activeIndex: number,
    ): Promise<void> {
        if (this.isMobile) {
            this.showRightMobile = true;
        }
        $event.stopPropagation();
        this.selectedIndex = activeIndex;
        this.selectedItem = this.documentsList[this.selectedIndex];

        this.downloadLink = null;
        this.activeImgInBase64 = null;
        this.fileNameWithExt = null;

        if (this.abortController) this.abortController.abort();
        const currentController = new AbortController();
        this.abortController = currentController;

        this.load_document = true;

        try {
            const result: IDocumentResult = await lastValueFrom(
                this.fileServices.getDocument(
                    id,
                    file_name,
                    this.abortController.signal,
                ),
            );
            if (!this.abortController) return;
            if (this.isEnumValue(ImgTypes, result.fileType)) {
                await this.openImg(result.fileBuffer.data, result.fileType);
            } else if (this.isEnumValue(DownloadTypes, result.fileType)) {
                this.createUrlForDownload(
                    result.fileBuffer.data,
                    result.fileType,
                );
            } else {
                throw new Error('Неизвестный тип документа');
            }
            this.fileNameWithExt = file_name.toLowerCase();
        } catch (e: any) {
            if (e.name === 'AbortError') {
                return;
            }
        } finally {
            if (!currentController.signal.aborted) {
                this.load_document = false;
            }
        }
    }

    public async _arrayBufferToBase64(buffer: Buffer): Promise<string> {
        let binary = '';
        const bytes = new Uint8Array(buffer);
        const len = bytes.byteLength;
        for (let i = 0; i < len; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return window.btoa(binary);
    }
    public async openImg(filebuffer: Buffer, filetype: string): Promise<void> {
        this.activeImgInBase64 = `data:image/${filetype};base64,${await this._arrayBufferToBase64(
            filebuffer,
        )}`;

        this.createUrlForDownload(filebuffer, filetype);
    }
    public isEnumValue(
        enumObj: Record<string, string>,
        value: string,
    ): boolean {
        return Object.values(enumObj).includes(value.toLowerCase());
    }
    public createUrlForDownload(filebuffer: Buffer, filetype: string): void {
        try {
            const bytes = new Uint8Array(filebuffer);
            const blob = new Blob([bytes], {
                type: filetype,
            });
            const url = window.URL.createObjectURL(blob);
            this.downloadLink = url;
        } catch (e) {
            throw e;
        }
    }

    protected changedPage(event: any) {
        this.documentListOnPage = this.documentsList.slice(
            0,
            this.documentLimitOnPage,
        );

        const start: number = this.documentLimitOnPage * event;
        const end: number = start + this.documentLimitOnPage;
        this.documentListOnPage = this.documentsList.slice(start, end);
    }

    // Вспомогательный метод для парсинга строки даты
    private parseDateString(dateStr: string): Date | null {
        try {
            const [datePart, timePart] = dateStr.split(' ');
            const [day, month, year] = datePart.split('.').map(Number);
            const [hours, minutes] = timePart.split(':').map(Number);
            return new Date(year, month - 1, day, hours, minutes); // Месяцы в JS с 0
        } catch (e) {
            console.error('Ошибка парсинга даты:', e);
            return null;
        }
    }

    // Упрощенная версия методов
    public isBlockYear(index: number): boolean {
        if (index === 0) return true;

        return (
            this.documentsList.length >= index + 1 &&
            this.documentsList[index].dtSort.getFullYear() !==
                this.documentsList[index - 1].dtSort.getFullYear()
        );
    }

    public getYear(index: number): string {
        if (this.documentsList && this.documentsList[index]) {
            return this.documentsList[index].dtSort.getFullYear() + '';
        } else {
            return '';
        }
    }

    public getYearPC(index: number): string {
        const dateStr = this.documentsList[index]?.datStr;
        if (!dateStr) return '';
        const parsedDate = this.parseDateString(dateStr);
        return parsedDate?.getFullYear().toString() || '';
    }

    public getFileFirmat(filename: string): string {
        const cleanedFilename = filename.trim();
        const lastDotIndex = cleanedFilename.lastIndexOf('.');
        if (lastDotIndex <= 0 || lastDotIndex === cleanedFilename.length - 1) {
            return '';
        }
        return cleanedFilename.substring(lastDotIndex + 1).toLowerCase();
    }

    public async downloadFile(
        $event: any,
        id: number,
        file_name: string,
        activeIndex: number,
    ): Promise<void> {
        $event.stopPropagation(); // Prevent event bubbling

        this.load_document = true;

        try {
            const result: IDocumentResult = await lastValueFrom(
                this.fileServices.getDocument(id, file_name),
            );

            // Convert buffer to Blob and trigger download
            const bytes = new Uint8Array(result.fileBuffer.data);
            const blob = new Blob([bytes], { type: result.fileType });
            const url = window.URL.createObjectURL(blob);

            // Create temporary <a> element for download
            const link = document.createElement('a');
            link.href = url;
            link.download = file_name.toLowerCase();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url); // Clean up

            this.alertService.success('Документ успешно загружен');
        } catch (e) {
            console.error(e);
            this.alertService.error(
                e instanceof Error ? e.message : 'Ошибка загрузки',
            );
        } finally {
            this.load_document = false;
        }
    }

    public handleClose() {
        this.selectedIndex = null; // Сбрасываем выбранный индекс
        this.downloadLink = null; // Очищаем ссылку на скачивание
        this.activeImgInBase64 = null; // Очищаем изображение
    }
}
